import ChevronRight from '@ixigo/icons/ChevronRight';
import Collapse from '@ixigo/iui/Collapse';
import { IconButton } from '@ixigo/iui/IconButton';
import cn from '@repo/shared/utils/cn';
import { useState } from 'react';
import type { BaseFareSummary } from '@/api';
import { SectionHeader } from '../section-header';
import { FareItemCollapsibleGroup, FareItemGroup } from './FareGroup';
import type { DiscountFareItem } from './index.types';
import { LineItem } from './LineItem';

export interface FareSummaryProps {
	displayCharges: BaseFareSummary[];
	discounts?: DiscountFareItem[];
	paidCharges?: BaseFareSummary[];
	toBePaidCharges?: BaseFareSummary[];

	// TODO: enum value should not be PascalCase. also when two values are possible, use a boolean instead
	mode?: 'view' | 'Collapsible';
	expanded?: boolean;
	children?: React.ReactNode;
	className?: string;
	/**
	 * optional header component displayed below section title
	 */
	header?: React.ReactNode;
}

export function FareSummary({
	displayCharges = [],
	discounts = [],
	paidCharges = [],
	toBePaidCharges = [],
	mode = 'view',
	expanded = true,
	children = null,
	header,
	className = '',
}: FareSummaryProps) {
	const [isExpanded, setIsExpanded] = useState(
		mode !== 'Collapsible' || expanded,
	);

	return (
		<div className={cn('bg-primary flex flex-col gap-y-20 p-20', className)}>
			<div className={cn('flex flex-col')} id="fare-summary">
				<div className="flex items-center justify-between">
					<SectionHeader title="Fare Summary" />
					{mode === 'Collapsible' && (
						<IconButton onClick={() => setIsExpanded(!isExpanded)} size="sm">
							<ChevronRight
								className={cn(
									'transition-transform duration-300',
									isExpanded ? '-rotate-90' : 'rotate-90',
								)}
							/>
						</IconButton>
					)}
				</div>
				{header}
				<div className={cn('flex flex-col', isExpanded ? 'gap-y-10' : '')}>
					{paidCharges.length > 0 && (
						<FareItemGroup className="border-none pt-0">
							{paidCharges.map((item, index) => (
								<LineItem key={index} {...item} size="lg" />
							))}
						</FareItemGroup>
					)}
					<Collapse in={isExpanded}>
						<div
							className={cn(
								'flex flex-col py-10',
								paidCharges.length > 0
									? 'border-t border-tertiary'
									: 'border-none',
							)}
						>
							{displayCharges.map((item, index) => {
								if (item.breakup?.length) {
									return (
										<FareItemCollapsibleGroup
											label={item.label}
											value={item.value}
											key={index}
										>
											{item.breakup.map((breakUpItem, breakUpIndex) => (
												<LineItem key={breakUpIndex} {...breakUpItem} />
											))}
										</FareItemCollapsibleGroup>
									);
								}
								return (
									<LineItem key={index} label={item.label} value={item.value} />
								);
							})}
						</div>
						{discounts.length > 0 && (
							<FareItemGroup>
								{discounts.map((item, index) => (
									<LineItem key={index} {...item} />
								))}
							</FareItemGroup>
						)}
					</Collapse>
					{toBePaidCharges.length > 0 && (
						<FareItemGroup className={cn(isExpanded ? '' : 'border-none')}>
							{toBePaidCharges.map((item, index) => (
								<LineItem key={index} {...item} size="lg" />
							))}
						</FareItemGroup>
					)}
				</div>
			</div>
			{children}
		</div>
	);
}

export default FareSummary;
